import { useEffect, useState } from 'react';
import '../../../styles/pages/notifications/insightsSettings.scss';
import { useTranslation } from 'react-i18next';
import {
  calendarSVG,
  chatSVG,
  clockSVG,
  messageSVG,
  webhookSVG,
  whatsappSVG,
} from '../../../lib/allCustomSVGs';
import AINormalInput from '../../../ui/inputs/AINormalInput';
import update_insight_preferences_api_call from '../../../lib/api/tara/notifications/settings/update_insight_preferences_api_call';
import update_insight_delivery_options_api_call from '../../../lib/api/tara/notifications/settings/update_insight_delivery_options_api_call';
import SettingsIconSelectInput from '../../../ui/inputs/SettingsIconSelectInput';
import SettingsCommonCard from '../../../ui/cards/SettingsCommonCard';
import '../../../styles/pages/settings/settings.scss';
import '../../../styles/generic/normalForm.scss';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import MultiInput from '../../../ui/inputs/MultiInput';

interface Props {
  allPreferences: any;
  allDeliveryOptions: any[];
  setAllPreferences: (preferences: any) => void;
  setAllDeliveryOptions: (options: any[]) => void;
}

const NotificationsSettings = ({
  allPreferences,
  allDeliveryOptions,
  setAllPreferences,
  setAllDeliveryOptions,
}: Props) => {
  const { t } = useTranslation();

  // ---------------- Preferences (auto-save like reference) ----------------
  const [medium, setMedium] = useState(
    allPreferences?.default_notification_method || 'email'
  );
  const [time, setTime] = useState(
    allPreferences?.delivery_frequency || 'daily'
  );
  const [isMonitoringEnabled, setIsMonitoringEnabled] = useState(
    allPreferences?.monitoring_enabled || false
  );

  // which pref keys are updating (UI disables while any is updating)
  const [updatingPreferenceKeys, setUpdatingPreferenceKeys] = useState<
    Set<
      | 'default_notification_method'
      | 'delivery_frequency'
      | 'monitoring_enabled'
    >
  >(new Set());

  const isAnyPrefUpdating = updatingPreferenceKeys.size > 0;
  const isKeyUpdating = (
    k:
      | 'default_notification_method'
      | 'delivery_frequency'
      | 'monitoring_enabled'
  ) => updatingPreferenceKeys.has(k);

  const updatePreferences = async (
    partial: Partial<{
      default_notification_method: string;
      delivery_frequency: string;
      monitoring_enabled: boolean;
    }>,
    key:
      | 'default_notification_method'
      | 'delivery_frequency'
      | 'monitoring_enabled'
  ) => {
    setUpdatingPreferenceKeys((prev) => new Set(prev).add(key));
    try {
      // build payload from current state + partial change
      const payload = {
        default_notification_method:
          partial.default_notification_method ?? medium,
        delivery_frequency: partial.delivery_frequency ?? time,
        monitoring_enabled: partial.monitoring_enabled ?? isMonitoringEnabled,
      };
      const data = await update_insight_preferences_api_call(payload);
      if (data) {
        setAllPreferences(data);
        // sync local state with server echo if present
        if (typeof data.default_notification_method === 'string') {
          setMedium(data.default_notification_method);
        }
        if (typeof data.delivery_frequency === 'string') {
          setTime(data.delivery_frequency);
        }
        if (typeof data.monitoring_enabled === 'boolean') {
          setIsMonitoringEnabled(data.monitoring_enabled);
        }
      }
    } catch (e) {
      console.error('Failed to update preferences', e);
    } finally {
      setUpdatingPreferenceKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // ---------------- Delivery options ----------------
  const [selectedEmail, setSelectedEmail] = useState('');
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [secondaryEmail, setSecondaryEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [slackURL, setSlackURL] = useState('');
  const [emailFilterOptions, setEmailFilterOptions] = useState<any[]>([]);
  const [isDeliveryOptionsSubmitting, setIsDeliveryOptionsSubmitting] =
    useState(false);

  // ▼ Added for webhooks
  const [webhookURLs, setWebhookURLs] = useState<string[]>([]);

  const isMediumEmail = medium === 'email';
  const isMediumWhatsApp = medium === 'whatsapp';
  const isMediumSlack = medium === 'slack';
  const isMediumWebhook = medium === 'webhook';

  const trimmedSlackURL = typeof slackURL === 'string' ? slackURL.trim() : '';
  const isSlackURLPresent = trimmedSlackURL.length > 0;
  const trimmedWebhookURLs = webhookURLs
    .map((url) => (typeof url === 'string' ? url.trim() : ''))
    .filter((url) => url.length > 0);
  const isWebhookURLsPresent = trimmedWebhookURLs.length > 0;

  const shouldShowSaveButton =
    isMediumSlack || (isMediumWebhook && webhookURLs.length > 0);
  const isSaveDisabled =
    isDeliveryOptionsSubmitting ||
    (isMediumSlack && !isSlackURLPresent) ||
    (isMediumWebhook && !isWebhookURLsPresent);

  // Merge-hydrate delivery options (preserve fields not present in payload)
  useEffect(() => {
    if (!Array.isArray(allDeliveryOptions)) return;

    let nextEmail = '';
    let nextSecondaryEmail = '';
    let nextWhatsapp = '';
    let nextSlack = '';

    let nextWebhookURLs: string[] = [];
    let seenEmail = false;
    let seenSecondary = false;
    let seenWhatsapp = false;
    let seenSlack = false;
    let seenWebhook = false;

    for (const item of allDeliveryOptions) {
      if (!item || typeof item !== 'object') continue;
      const channel = item.channel;
      const value = item.config?.value;

      if (channel === 'email' && typeof value === 'string') {
        seenEmail = true;
        nextEmail = value;
      } else if (channel === 'secondary_email' && typeof value === 'string') {
        seenSecondary = true;
        nextSecondaryEmail = value;
      } else if (channel === 'whatsapp' && typeof value === 'string') {
        seenWhatsapp = true;
        nextWhatsapp = value;
      } else if (channel === 'slack' && typeof value === 'string') {
        seenSlack = true;
        nextSlack = value;
      } else if (channel === 'webhook') {
        seenWebhook = true;
        if (Array.isArray(value)) {
          nextWebhookURLs = value.filter((v) => typeof v === 'string');
        } else if (typeof value === 'string') {
          nextWebhookURLs = [value];
        }
      }
    }

    const mergedPrimary = seenEmail ? nextEmail : primaryEmail;
    const mergedSecondary = seenSecondary ? nextSecondaryEmail : secondaryEmail;
    const mergedPhone = seenWhatsapp ? nextWhatsapp : phone;
    const mergedSlack = seenSlack ? nextSlack : slackURL;

    setPrimaryEmail(mergedPrimary);
    setSecondaryEmail(mergedSecondary);
    setPhone(mergedPhone);
    setSlackURL(mergedSlack);

    // ▼ Apply merged webhook values
    if (seenWebhook) {
      setWebhookURLs(nextWebhookURLs);
    }

    const options: any[] = [];
    if (mergedPrimary)
      options.push({ text: mergedPrimary, value: mergedPrimary, icon: null });
    if (mergedSecondary)
      options.push({
        text: mergedSecondary,
        value: mergedSecondary,
        icon: null,
      });
    setEmailFilterOptions(options);

    if (!selectedEmail) {
      setSelectedEmail(mergedPrimary || mergedSecondary || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDeliveryOptions]);

  const getEmailValue = () => {
    if (selectedEmail?.trim()) return selectedEmail.trim();
    if (primaryEmail?.trim()) return primaryEmail.trim();
    if (secondaryEmail?.trim()) return secondaryEmail.trim();
    return '';
  };

  // ---------------- Delivery submit (Slack + Webhook) ----------------
  const handleSaveDeliveryOptions = async () => {
    if (isDeliveryOptionsSubmitting) return;

    const delivery_options: Array<{
      channel: string;
      config: { value: any };
    }> = [];

    if (isMediumSlack) {
      if (!isSlackURLPresent) return;
      delivery_options.push({
        channel: 'slack',
        config: { value: trimmedSlackURL },
      });
    }

    if (isMediumWebhook) {
      if (!isWebhookURLsPresent) return;
      delivery_options.push({
        channel: 'webhook',
        config: { value: trimmedWebhookURLs },
      });
    }

    if (delivery_options.length === 0) return;

    setIsDeliveryOptionsSubmitting(true);
    try {
      const data = await update_insight_delivery_options_api_call({
        delivery_options,
      });

      if (data) {
        setAllDeliveryOptions(data);
        // keep local slack if API omits it
        setSlackURL((prev) => (prev ? prev : slackURL));
      }
    } catch (error) {
      console.error('Failed to update delivery options', error);
    } finally {
      setIsDeliveryOptionsSubmitting(false);
    }
  };

  // ---------------- UI ----------------
  const renderPreferencesContent = () => {
    return (
      <SettingsCommonCard mainTitle={t('NotificationPreferences')}>
        <div className='normalForm'>
          <div className='normalForm__top'>
            <div>
              <SettingsIconSelectInput
                filterOptions={[
                  { text: t('Email'), value: 'email', icon: messageSVG },
                  { text: t('WhatsApp'), value: 'whatsapp', icon: whatsappSVG },
                  { text: t('Slack'), value: 'slack', icon: chatSVG },
                  { text: t('Webhook'), value: 'webhook', icon: webhookSVG },
                ]}
                value={medium}
                onFilterChange={(val) => {
                  setMedium(val);
                  // auto-save on change
                  updatePreferences(
                    { default_notification_method: val },
                    'default_notification_method'
                  );
                }}
                title={t('NotificationMedium')}
                readonly={isAnyPrefUpdating}
                loading={isKeyUpdating('default_notification_method')}
              />
            </div>
            <div>
              <SettingsIconSelectInput
                marginTop='1.5rem'
                filterOptions={[
                  { text: t('Daily'), value: 'daily', icon: clockSVG },
                  { text: t('Weekly'), value: 'weekly', icon: calendarSVG },
                  { text: t('Monthly'), value: 'monthly', icon: calendarSVG },
                ]}
                value={time}
                title={t('Frequency')}
                onFilterChange={(val) => {
                  setTime(val);
                  // auto-save on change
                  updatePreferences(
                    { delivery_frequency: val },
                    'delivery_frequency'
                  );
                }}
                readonly={isAnyPrefUpdating}
                loading={isKeyUpdating('delivery_frequency')}
              />
            </div>
          </div>
          {/* No Save button for Preferences (auto-save like your reference) */}
        </div>
      </SettingsCommonCard>
    );
  };

  const renderDeliveryOptionsContent = () => {
    return (
      <SettingsCommonCard mainTitle={t('DeliveryOptions')}>
        <div className='normalForm'>
          <div className='normalForm__top'>
            {isMediumEmail && (
              <div>
                {/* Email: read-only (not sent to API) */}
                <SettingsIconSelectInput
                  filterOptions={emailFilterOptions}
                  value={getEmailValue()}
                  title={t('EmailAddress')}
                  onFilterChange={(val) => setSelectedEmail(val)}
                  hideTick={true}
                />
              </div>
            )}

            {isMediumWhatsApp && (
              <div>
                {/* WhatsApp: read-only (not sent to API) */}
                <AINormalInput
                  changeListeners={[
                    (e) => {
                      setPhone(e.target.value);
                    },
                  ]}
                  id='notification-form-phone'
                  value={phone}
                  placeholder={t('WhatsAppPhoneNumber')}
                  innerPlaceholder='+1234567890'
                  required={true}
                  pattern='^[0-9]{4,}$'
                  readonly={true}
                />
              </div>
            )}

            {isMediumSlack && (
              <div>
                {/* Slack webhook URL (editable & sent) */}
                <AINormalInput
                  changeListeners={[
                    (e) => {
                      setSlackURL(e.target.value);
                    },
                  ]}
                  id='notification-form-slack'
                  value={slackURL}
                  placeholder={t('SlackWebhookURL')}
                  innerPlaceholder='https://hooks.slack.com/...'
                  required={true}
                />
              </div>
            )}

            {isMediumWebhook && (
              <div>
                {/* ▼ Webhook URLs list (editable & sent as array) */}
                <MultiInput
                  mainTitle='WebhookURLs'
                  mainButtonText='AddWebhook'
                  inputPlaceholder='WebhookURL'
                  values={webhookURLs}
                  onChange={(values) => setWebhookURLs(values)}
                />
              </div>
            )}
          </div>

          {shouldShowSaveButton && (
            <div
              className='normalForm__bottom flex-start'
              style={{
                marginTop: '2rem',
                opacity: isSaveDisabled ? 0.5 : 1,
              }}
            >
              <MainSquareButton
                value={t('Save')}
                type='button'
                disabled={isSaveDisabled}
                loading={isDeliveryOptionsSubmitting}
                onClick={handleSaveDeliveryOptions}
              />
            </div>
          )}
        </div>
      </SettingsCommonCard>
    );
  };

  return (
    <div>
      {renderPreferencesContent()}
      <div style={{ marginTop: '2rem' }} />
      {renderDeliveryOptionsContent()}
    </div>
  );
};

export default NotificationsSettings;
